import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH
} from "../components/map/defines";
import type { Shape, SpotShape, WallPathEdge } from "../types";

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};
const rotate = (x: number, y: number, a: number) => ({
  x: x * Math.cos(a) - y * Math.sin(a),
  y: x * Math.sin(a) + y * Math.cos(a)
});

/** Imported desk geometry is temporarily in image pixels. Fit the map to the
 * editor's fixed desk, never stretch an individual bookable resource. */
export function calibrateImportedDesks(map: MapCanvasValue): MapCanvasValue {
  const desks = map.shapes
    .filter((s): s is SpotShape => s.type === "spot" && s.spotType === "desk")
    .map((shape) => {
      const sx = shape.scaleX ?? 1,
        sy = shape.scaleY ?? 1;
      const angle = ((shape.rotation || 0) * Math.PI) / 180;
      const offset = rotate(
        45 * sx,
        (shape.orientation === "bottom" ? 35 : 65) * sy,
        angle
      );
      const width = 90 * sx,
        depth = 50 * sy;
      // The visible tabletop center is invariant when turning a vertical detection
      // to the standard horizontal desk glyph.
      const rotation = (shape.rotation || 0) + (depth > width ? 90 : 0);
      const a = (rotation * Math.PI) / 180;
      return {
        shape,
        center: { x: shape.x + offset.x, y: shape.y + offset.y },
        width: Math.max(width, depth),
        depth: Math.min(width, depth),
        rotation,
        a
      };
    });
  if (!desks.length) return map;
  const tabletopScale = Math.max(
    median(desks.map((d) => 90 / d.width)),
    median(desks.map((d) => 50 / d.depth))
  );
  let scale = tabletopScale;
  // Tabletop dimensions alone can leave facing availability markers overlapping
  // across an aisle. Their size stays fixed too: enlarge the whole map, never
  // move individual resources or shrink their glyphs. Solve the separation in
  // image coordinates so applying calibration again is stable.
  const markers = desks.map((desk) => ({
    center: desk.center,
    offset: rotate(0, desk.shape.orientation === "bottom" ? 33 : -33, desk.a),
    radius:
      (desk.shape.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS) +
      (desk.shape.circleProps?.strokeWidth ||
        DEFAULT_NODE_CIRCLE_STROKE_WIDTH) /
        2
  }));
  for (let i = 0; i < markers.length; i++) {
    for (let j = i + 1; j < markers.length; j++) {
      const left = markers[i],
        right = markers[j];
      const dx = right.center.x - left.center.x;
      const dy = right.center.y - left.center.y;
      const ox = right.offset.x - left.offset.x;
      const oy = right.offset.y - left.offset.y;
      const a = dx * dx + dy * dy;
      // Coincident detections cannot be separated by a global scale.
      if (a < 1e-8) continue;
      const clearance = left.radius + right.radius + 4;
      const b = 2 * (dx * ox + dy * oy);
      const c = ox * ox + oy * oy - clearance * clearance;
      const discriminant = b * b - 4 * a * c;
      if (discriminant <= 0) continue;
      const separated = (-b + Math.sqrt(discriminant)) / (2 * a);
      scale = Math.max(scale, separated);
    }
  }
  // A near-duplicate detection can demand a huge spacing correction. In that
  // case marker spacing is not reliable scale evidence: keep the tabletop fit.
  // Reject the entire correction instead of filtering individual pairs, which
  // could make repeated calibration enlarge the map a little more each time.
  if (scale > tabletopScale * 1.5) scale = tabletopScale;
  // Calibrate against the tabletop, not the editor selection/interaction box.
  // Back-to-back tabletops can touch while their chairs face outward.
  const byId = new Map(desks.map((d) => [d.shape.id, d]));
  const length = (value: number | undefined) =>
    value === undefined ? undefined : value * scale;
  const edge = (value: WallPathEdge): WallPathEdge => ({
    ...value,
    curvature: length(value.curvature),
    doors: value.doors?.map((d) => ({ ...d, width: d.width * scale })),
    windows: value.windows?.map((w) => ({ ...w, width: w.width * scale }))
  });
  const shapes = map.shapes.map((shape): Shape => {
    const desk = byId.get(shape.id);
    if (desk && shape.type === "spot") {
      const offset = rotate(
        45,
        desk.shape.orientation === "bottom" ? 35 : 65,
        desk.a
      );
      return {
        ...shape,
        x: desk.center.x * scale - offset.x,
        y: desk.center.y * scale - offset.y,
        width: 100,
        height: 100,
        scaleX: 1,
        scaleY: 1,
        rotation: ((((desk.rotation + 180) % 360) + 360) % 360) - 180,
        orientation: desk.shape.orientation === "bottom" ? "bottom" : "top",
        textProps: { ...shape.textProps, fontSize: 14 }
      };
    }
    const base = {
      ...shape,
      x: shape.x * scale,
      y: shape.y * scale,
      width: shape.width * scale,
      height: shape.height * scale,
      cornerRadius: length(shape.cornerRadius)
    };
    if (shape.strokeWidth !== undefined)
      base.strokeWidth = shape.strokeWidth * scale;
    if (shape.type === "wall-path")
      return {
        ...base,
        type: "wall-path",
        vertices: shape.vertices.map((v) => ({
          ...v,
          x: v.x * scale,
          y: v.y * scale,
          cornerRadius: length(v.cornerRadius)
        })),
        edges: shape.edges.map(edge),
        thickness: shape.thickness * scale
      };
    if (shape.type === "wall")
      return {
        ...base,
        curvature: length(shape.curvature),
        doors: edge(shape).doors,
        windows: edge(shape).windows,
        type: "wall",
        x2: shape.x2 * scale,
        y2: shape.y2 * scale,
        thickness: shape.thickness * scale
      };
    // Rooms and parking glyphs have their own local geometry; keep that geometry
    // and apply the map scale once through their existing transform.
    if (shape.type === "spot")
      return {
        ...shape,
        x: base.x,
        y: base.y,
        scaleX: (shape.scaleX ?? 1) * scale,
        scaleY: (shape.scaleY ?? 1) * scale
      };
    if (shape.type === "dynamic-furniture")
      return {
        ...base,
        type: "dynamic-furniture",
        layout: {
          ...shape.layout,
          tableWidth: shape.layout.tableWidth * scale,
          tableHeight: shape.layout.tableHeight * scale,
          chairScale: shape.layout.chairScale * scale,
          gap: shape.layout.gap * scale,
          cornerRadius: shape.layout.cornerRadius * scale
        }
      };
    if (shape.type === "circle")
      return { ...base, type: "circle", radius: shape.radius * scale };
    if (shape.type === "text")
      return {
        ...base,
        type: "text",
        text: shape.text,
        fontSize: length(shape.fontSize)
      };
    return base;
  });
  return {
    ...map,
    shapes,
    editingZoneWidth: map.editingZoneWidth * scale,
    editingZoneHeight: map.editingZoneHeight * scale,
    pixelsPerMeter:
      map.pixelsPerMeter == null
        ? map.pixelsPerMeter
        : map.pixelsPerMeter * scale
  };
}
